import React, { useState } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Typography,
  Toolbar,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  Chip,
  Skeleton,
  LinearProgress,
} from "@mui/material";
import {
  Search as SearchIcon,
  FilterList as FilterListIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";
import { visuallyHidden } from "@mui/utils";
import { Link as RouterLink } from "react-router-dom";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import { DataTableProps, TableColumn } from "../../types";

// Helper function to get value from an object using dot notation
function getNestedValue(obj: any, path: string): any {
  const keys = path.split(".");
  return keys.reduce(
    (acc, key) => (acc && acc[key] !== undefined ? acc[key] : null),
    obj,
  );
}

interface CellContentProps {
  column: TableColumn;
  row: any;
}

// Component for rendering cell content based on type
const CellContent = ({
  column,
  row,
}: CellContentProps): React.ReactElement | null => {
  const value = getNestedValue(row, column.field);

  if (value === null || value === undefined) {
    return (
      <Typography variant="body2" color="text.disabled">
        —
      </Typography>
    );
  }

  switch (column.type) {
    case "date":
      return (
        <Typography variant="body2">
          {new Date(value).toLocaleDateString()}
        </Typography>
      );
    case "datetime":
      return (
        <Typography variant="body2">
          {new Date(value).toLocaleString()}
        </Typography>
      );
    case "boolean":
      return (
        <Chip
          size="small"
          label={value ? "Yes" : "No"}
          color={value ? "success" : "default"}
          variant={value ? "filled" : "outlined"}
        />
      );
    case "status":
      let color: "success" | "primary" | "warning" | "error" | "default" =
        "default";
      switch (String(value).toLowerCase()) {
        case "active":
        case "completed":
        case "success":
          color = "success";
          break;
        case "pending":
        case "in progress":
        case "running":
          color = "primary";
          break;
        case "warning":
        case "delayed":
          color = "warning";
          break;
        case "error":
        case "failed":
          color = "error";
          break;
        default:
          color = "default";
      }
      return <Chip size="small" label={value} color={color} />;
    case "chip":
      return <Chip size="small" label={value} variant="outlined" />;
    case "chipArray":
      if (!Array.isArray(value)) return null;
      return (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
          {value.slice(0, 3).map((item, index) => (
            <Chip key={index} size="small" label={item} variant="outlined" />
          ))}
          {value.length > 3 && (
            <Chip
              size="small"
              label={`+${value.length - 3}`}
              variant="outlined"
            />
          )}
        </Box>
      );
    case "link":
      return (
        <Typography
          variant="body2"
          component={RouterLink}
          to={column.linkPath ? column.linkPath(row) : "#"}
          color="primary"
          sx={{
            textDecoration: "none",
            "&:hover": { textDecoration: "underline" },
          }}
        >
          {value}
        </Typography>
      );
    case "custom":
      if (column.render) {
        const rendered = column.render(row);
        // Ensure we're returning a valid React element
        return rendered as React.ReactElement;
      }
      return <Typography variant="body2">{String(value)}</Typography>;
    default:
      return <Typography variant="body2">{String(value)}</Typography>;
  }
};

interface DataTableToolbarProps {
  title?: string;
  filterText: string;
  onFilterChange: (value: string) => void;
}

// A component for the table's toolbar
const DataTableToolbar: React.FC<DataTableToolbarProps> = ({
  title,
  filterText,
  onFilterChange,
}) => {
  return (
    <Toolbar
      sx={{
        pl: { sm: 2 },
        pr: { xs: 1, sm: 1 },
      }}
    >
      <Typography
        sx={{ flex: "1 1 100%" }}
        variant="h6"
        id="tableTitle"
        component="div"
      >
        {title}
      </Typography>

      <TextField
        variant="outlined"
        size="small"
        placeholder="Search..."
        value={filterText}
        onChange={(e) => onFilterChange(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
        sx={{ width: 300 }}
      />

      <Tooltip title="Filter list">
        <IconButton>
          <FilterListIcon />
        </IconButton>
      </Tooltip>
    </Toolbar>
  );
};

// Main DataTable component
const DataTable: React.FC<DataTableProps> = ({
  title,
  data = [],
  columns = [],
  loading = false,
  rowsPerPageOptions = [10, 25, 50, 100],
  defaultSort = { field: "id", direction: "asc" },
  onRowClick,
  onEdit,
  onDelete,
  onView,
  getRowId = (row) => row.id || row._id,
  disableActions = false,
}) => {
  // State variables
  const [order, setOrder] = useState<"asc" | "desc">(defaultSort.direction);
  const [orderBy, setOrderBy] = useState<string>(defaultSort.field);
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(rowsPerPageOptions[0]);
  const [filter, setFilter] = useState<string>("");
  const [actionMenuAnchor, setActionMenuAnchor] = useState<null | HTMLElement>(
    null,
  );
  const [selectedRow, setSelectedRow] = useState<any | null>(null);

  // Handle sort request
  const handleRequestSort = (
    _event: React.MouseEvent<unknown>,
    property: string,
  ) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  // Handle pagination
  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle row click
  const handleRowClick = (_event: React.MouseEvent<unknown>, row: any) => {
    if (onRowClick) {
      onRowClick(row);
    }
  };

  // Handle action menu
  const handleActionClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    row: any,
  ) => {
    event.stopPropagation();
    setSelectedRow(row);
    setActionMenuAnchor(event.currentTarget);
  };

  const handleActionClose = () => {
    setActionMenuAnchor(null);
    setSelectedRow(null);
  };

  const handleEdit = () => {
    if (onEdit && selectedRow) {
      onEdit(selectedRow);
    }
    handleActionClose();
  };

  const handleDelete = () => {
    if (onDelete && selectedRow) {
      onDelete(selectedRow);
    }
    handleActionClose();
  };

  const handleView = () => {
    if (onView && selectedRow) {
      onView(selectedRow);
    }
    handleActionClose();
  };

  // Filter data based on search input
  const filteredData = filter
    ? data.filter((row) => {
        return columns.some((column) => {
          const value = getNestedValue(row, column.field);
          if (value === null || value === undefined) return false;
          return String(value).toLowerCase().includes(filter.toLowerCase());
        });
      })
    : data;

  // Sort data
  const sortedData = filteredData.sort((a, b) => {
    const valueA = getNestedValue(a, orderBy);
    const valueB = getNestedValue(b, orderBy);

    if (valueA === null || valueA === undefined)
      return order === "asc" ? -1 : 1;
    if (valueB === null || valueB === undefined)
      return order === "asc" ? 1 : -1;

    if (typeof valueA === "number" && typeof valueB === "number") {
      return order === "asc" ? valueA - valueB : valueB - valueA;
    }

    return order === "asc"
      ? String(valueA).localeCompare(String(valueB))
      : String(valueB).localeCompare(String(valueA));
  });

  // Paginate data
  const paginatedData = sortedData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  // Loading skeletons
  const loadingRows = Array.from({ length: rowsPerPage }).map((_, index) => (
    <TableRow key={`skeleton-${index}`}>
      {columns.map((column, colIndex) => (
        <TableCell key={`skeleton-cell-${colIndex}`}>
          <Skeleton variant="text" />
        </TableCell>
      ))}
      {!disableActions && (
        <TableCell align="right">
          <Skeleton variant="circular" width={24} height={24} />
        </TableCell>
      )}
    </TableRow>
  ));

  return (
    <Paper sx={{ width: "100%", mb: 2, borderRadius: 2, overflow: "hidden" }}>
      <DataTableToolbar
        title={title}
        filterText={filter}
        onFilterChange={setFilter}
      />

      {loading && <LinearProgress sx={{ height: 3 }} />}

      <TableContainer>
        <Table
          sx={{ minWidth: 750 }}
          aria-labelledby="tableTitle"
          size="medium"
        >
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.field}
                  align={column.align || "left"}
                  padding={column.disablePadding ? "none" : "normal"}
                  sortDirection={orderBy === column.field ? order : false}
                  sx={{ fontWeight: "bold", whiteSpace: "nowrap" }}
                >
                  <TableSortLabel
                    active={orderBy === column.field}
                    direction={orderBy === column.field ? order : "asc"}
                    onClick={(event) => handleRequestSort(event, column.field)}
                  >
                    {column.headerName}
                    {orderBy === column.field ? (
                      <Box component="span" sx={visuallyHidden}>
                        {order === "desc"
                          ? "sorted descending"
                          : "sorted ascending"}
                      </Box>
                    ) : null}
                  </TableSortLabel>
                </TableCell>
              ))}
              {!disableActions && <TableCell align="right">Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading
              ? loadingRows
              : paginatedData.map((row) => {
                  const rowId = getRowId(row);
                  return (
                    <TableRow
                      hover
                      onClick={(event) => handleRowClick(event, row)}
                      role="checkbox"
                      tabIndex={-1}
                      key={rowId}
                      sx={{
                        cursor: onRowClick ? "pointer" : "default",
                        "&:last-child td, &:last-child th": { border: 0 },
                      }}
                    >
                      {columns.map((column) => (
                        <TableCell
                          key={`${rowId}-${column.field}`}
                          align={column.align || "left"}
                        >
                          <CellContent column={column} row={row} />
                        </TableCell>
                      ))}
                      {!disableActions && (
                        <TableCell align="right">
                          <Tooltip title="Actions">
                            <IconButton
                              aria-label="actions"
                              onClick={(event) => handleActionClick(event, row)}
                              size="small"
                            >
                              <MoreVertIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
            {!loading && paginatedData.length === 0 && (
              <TableRow style={{ height: 53 * 5 }}>
                <TableCell
                  colSpan={columns.length + (disableActions ? 0 : 1)}
                  align="center"
                >
                  <Typography variant="body1" color="text.secondary">
                    {filter ? "No matching records found" : "No records found"}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={rowsPerPageOptions}
        component="div"
        count={filteredData.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />

      {/* Action Menu */}
      <Menu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={handleActionClose}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        {onView && (
          <MenuItem onClick={handleView}>
            <ListItemIcon>
              <VisibilityIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>View</ListItemText>
          </MenuItem>
        )}
        {onEdit && (
          <MenuItem onClick={handleEdit}>
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Edit</ListItemText>
          </MenuItem>
        )}
        {onDelete && (
          <MenuItem onClick={handleDelete}>
            <ListItemIcon>
              <DeleteIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText sx={{ color: "error.main" }}>Delete</ListItemText>
          </MenuItem>
        )}
      </Menu>
    </Paper>
  );
};

export default DataTable;
